import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Loader2, Sparkles } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { getUserWishlistV2 } from "../hooks/useWishlist";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import ProductGrid from "../components/_ResponsiveComponents/ProductCard";
import type { Product } from "../hooks/products";

/**
 * Maps a raw wishlist item (from RPC) to the standardized Product type
 * so ProductGrid / ProductCard can render it.
 */
const mapWishlistItemToProduct = (item: Record<string, any>): Product => ({
  id: item.id,
  name: item.name ?? "Untitled Product",
  description: item.description ?? "",
  price:
    item.on_sale && item.sale_rate
      ? item.usd_price * (1 - item.sale_rate / 100)
      : (item.usd_price ?? 0),
  original_price: item.usd_price ?? 0,
  image: "",
  images: item.image_paths ?? [],
  image_paths: item.image_paths ?? [],
  sale_rate: item.sale_rate ?? 0,
  on_sale: item.sale_rate > 0,
  is_featured: item.is_featured ?? false,
  quantity: item.quantity ?? 0,
  category_id: item.category_id,
  created_at: item.wishlist_added_at ?? item.created_at,
});

const WishlistPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, userId } = useAuth();

  const [rawItems, setRawItems] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/registration");
      return;
    }
    fetchWishlist();
  }, [user, userId, navigate]);

  const fetchWishlist = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const response = await getUserWishlistV2({ user_id: userId });
      setRawItems(response?.data?.items ?? []);
    } catch (error) {
      console.error("Error fetching wishlist:", error);
      setRawItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Map raw wishlist items → Product type for ProductGrid
  const products: Product[] = useMemo(
    () => rawItems.map(mapWishlistItemToProduct),
    [rawItems],
  );

  return (
    <>
      <Header />
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          {/* ── Page Title ── */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Heart className="w-5 h-5 text-primary fill-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white leading-tight">
                My Wishlist
              </h1>
              {!loading && products.length > 0 && (
                <p className="text-sm text-neutral-400 mt-0.5">
                  {products.length} {products.length === 1 ? "item" : "items"}{" "}
                  saved
                </p>
              )}
            </div>
          </div>

          {/* ── States ── */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-32">
              <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
              <p className="text-neutral-500 text-sm font-medium">
                Loading your wishlist…
              </p>
            </div>
          ) : products.length === 0 ? (
            /* ── Empty State ── */
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="relative mb-6">
                <div className="w-24 h-24 rounded-full bg-neutral-800/60 flex items-center justify-center">
                  <Heart className="w-10 h-10 text-neutral-600" />
                </div>
                <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                Nothing here yet
              </h2>
              <p className="text-neutral-400 text-sm max-w-sm mb-8">
                Start browsing and tap the heart icon on products you love.
                They'll show up right here.
              </p>
              <button
                onClick={() => navigate("/products")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-black rounded-xl hover:bg-primary-dark transition-all duration-200 font-semibold text-sm shadow-lg shadow-primary/20 hover:shadow-primary/30"
              >
                <ShoppingBag className="w-4 h-4" />
                Browse Products
              </button>
            </div>
          ) : (
            /* ── Product Grid ── */
            <ProductGrid
              products={products}
              viewMode="grid"
              loading={false}
              isShowWishlist={true}
            />
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default WishlistPage;
